import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Loader2, Save, Lock, Plus, Trash2, Megaphone, BarChart3, PlugZap } from 'lucide-react';
import { toast } from 'sonner';
import { serviceDeliveryAPI } from '../lib/api';

const TEXT_FIELDS = [
  { key: 'objetivo_campanas', label: 'Objetivo de las campañas', hint: 'Leads, ventas, tráfico, mensajes' },
  { key: 'oferta', label: 'Oferta que se anuncia', hint: 'Qué se promociona y a qué precio' },
  { key: 'presupuesto', label: 'Presupuesto mensual y su reparto' },
  { key: 'angulos_ganchos', label: 'Ángulos y ganchos que usamos' },
  { key: 'embudo', label: 'Embudo', hint: 'Recorrido: anuncio → landing/WhatsApp/perfil → cierre de venta' },
  { key: 'pixel_eventos', label: 'Píxel / eventos que medimos' },
  { key: 'kpis_objetivo', label: 'KPIs objetivo', hint: 'CPL, ROAS, CTR que buscamos' },
  { key: 'historico', label: 'Histórico', hint: 'Qué ha funcionado antes y qué no' },
];
const uid = () => Math.random().toString(36).slice(2, 10);
const eur = (n) => (n === null || n === undefined) ? '—' : `${Number(n).toLocaleString('es-ES', { maximumFractionDigits: 2 })} €`;

function MetaAdsPanel({ meta, canFin }) {
  if (!meta || !meta.connected) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center" data-testid="ads-meta-pending">
        <PlugZap className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm font-medium">{meta?.message || 'Pendiente de conexión con Meta Ads'}</p>
        <p className="text-xs text-muted-foreground mt-1">Los resultados reales (inversión, CPL, ROAS, CTR, leads) aparecerán aquí automáticamente cuando la app de Meta esté aprobada y la cuenta del cliente conectada.</p>
      </div>
    );
  }
  const ctr = (meta.impressions && meta.clicks) ? ((meta.clicks / meta.impressions) * 100) : null;
  const cards = [
    { label: 'Inversión', value: eur(meta.spend) },
    { label: 'Leads', value: meta.leads ?? '—' },
    { label: 'CPL', value: eur(meta.cost_per_lead) },
    { label: 'ROAS', value: '—' },
    { label: 'CTR', value: ctr === null ? '—' : `${ctr.toFixed(2)} %` },
    { label: 'Impresiones', value: meta.impressions?.toLocaleString('es-ES') ?? '—' },
  ].filter((c) => canFin || !['Inversión', 'CPL', 'ROAS'].includes(c.label));
  return (
    <div className="space-y-3" data-testid="ads-meta-real">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="rounded-lg border p-3 text-center">
            <p className="text-[11px] text-muted-foreground">{c.label}</p>
            <p className="text-lg font-semibold">{c.value}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Datos reales de Meta Ads · mes {meta.month}. ROAS requiere valor de conversión (no disponible aún).</p>
    </div>
  );
}

export default function AdsStrategySection({ clientId }) {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [canFin, setCanFin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await serviceDeliveryAPI.getStrategy(clientId, 'ads');
      setData(res?.data || {});
      setMeta(res?.meta_ads || { connected: false });
      setCanEdit(!!res?.can_edit);
      setCanFin(!!res?.can_view_financial);
      setDirty(false);
    } catch { toast.error('Error cargando la estrategia de ads'); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const setVal = (key, v) => { setData((p) => ({ ...p, [key]: v })); setDirty(true); };
  const setPublicos = (publicos) => { setData((p) => ({ ...p, publicos })); setDirty(true); };

  const handleSave = async () => {
    setSaving(true);
    try { await serviceDeliveryAPI.saveStrategy(clientId, 'ads', data); toast.success('Estrategia de ads guardada'); await load(); }
    catch (e) { toast.error(e?.message || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  if (loading || !data) return <div className="py-16 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  const RO = !canEdit;
  const publicos = Array.isArray(data.publicos) ? data.publicos : [];

  return (
    <div className="space-y-5" data-testid="ads-strategy-section">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2"><Megaphone className="w-5 h-5 text-primary" /> Estrategia de Ads</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">Planificación de las campañas de pago + resultados reales de Meta Ads.</p>
            </div>
            {RO ? <Badge variant="secondary" className="gap-1"><Lock className="w-3 h-3" /> Solo lectura</Badge> : (
              <Button onClick={handleSave} disabled={saving || !dirty} data-testid="ads-strategy-save">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{dirty ? 'Guardar' : 'Guardado'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {TEXT_FIELDS.slice(0, 2).map((f) => (
            <div key={f.key}>
              <Label>{f.label}</Label>
              {f.hint && <p className="text-xs text-muted-foreground mb-1">{f.hint}</p>}
              <Textarea value={data[f.key] || ''} onChange={(e) => setVal(f.key, e.target.value)} rows={2} disabled={RO} className="resize-none" data-testid={`ads-${f.key}`} />
            </div>
          ))}

          {/* Públicos */}
          <div>
            <div className="flex items-center justify-between">
              <Label>Públicos</Label>
              {!RO && <Button size="sm" variant="outline" onClick={() => setPublicos([...publicos, { id: uid(), texto: '' }])} data-testid="ads-add-publico"><Plus className="w-4 h-4 mr-1" /> Añadir público</Button>}
            </div>
            <p className="text-xs text-muted-foreground mb-2">Fríos, retargeting, lookalikes…</p>
            {publicos.length === 0 ? <p className="text-sm text-muted-foreground italic">Sin públicos.</p> : (
              <div className="space-y-2">
                {publicos.map((p) => (
                  <div key={p.id} className="flex items-center gap-2" data-testid={`ads-publico-${p.id}`}>
                    <Input value={p.texto || ''} onChange={(e) => setPublicos(publicos.map((x) => x.id === p.id ? { ...x, texto: e.target.value } : x))} placeholder="Ej: Retargeting visitantes web 30 días" disabled={RO} data-testid={`ads-publico-texto-${p.id}`} />
                    {!RO && <Button variant="ghost" size="icon" className="h-9 w-9 text-red-500 shrink-0" onClick={() => setPublicos(publicos.filter((x) => x.id !== p.id))} data-testid={`ads-del-publico-${p.id}`}><Trash2 className="w-4 h-4" /></Button>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {TEXT_FIELDS.slice(2).filter((f) => canFin || f.key !== 'presupuesto').map((f) => (
            <div key={f.key}>
              <Label>{f.label}</Label>
              {f.hint && <p className="text-xs text-muted-foreground mb-1">{f.hint}</p>}
              <Textarea value={data[f.key] || ''} onChange={(e) => setVal(f.key, e.target.value)} rows={2} disabled={RO} className="resize-none" data-testid={`ads-${f.key}`} />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Datos reales de Meta Ads */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base"><BarChart3 className="w-4 h-4 text-primary" /> Resultados reales de Meta Ads</CardTitle>
          <p className="text-xs text-muted-foreground">Objetivos (arriba, a mano) vs resultados reales (aquí, automáticos).</p>
        </CardHeader>
        <CardContent>
          <MetaAdsPanel meta={meta} canFin={canFin} />
        </CardContent>
      </Card>
    </div>
  );
}
